/**
 * B01 3D configurator — everything that does not need a product.
 *
 * Split out of product-3d-viewer.js, which was welded to the product gallery:
 * it returned early unless it found .product-gallery, read the variant form,
 * and hid the gallery's children to switch modes. None of that exists on a
 * page template, but almost everything else in that file — device tiering,
 * the WebXR probe, mounting, colour and material handling, the dimensions
 * overlay, the whole AR chain, the QR modal, the deep link — needs no product
 * at all.
 *
 * Extracted rather than copied because the AR path took about ten commits to
 * stabilise against real hardware. A second copy would drift, and every
 * future fix would have to be made twice.
 *
 * Callers: assets/product-3d-viewer.js (context 'product') and
 * assets/b01-page-viewer.js (context 'page'). Every analytics event emitted
 * here carries that context so the two surfaces can be told apart.
 */
(function () {
  'use strict';

  function create(opts) {
    var root = opts.root;
    var controls = opts.controls || null;
    var qrModal = opts.qrModal || null;
    var context = opts.context || 'product';
    // Called from handleDeepLink() so each caller can bring the viewer on
    // screen its own way — the product page has to leave photo mode first.
    var onDeepLink = opts.onDeepLink || null;

    var canvas = root.querySelector('[data-viewer-canvas]');
    var loadingEl = root.querySelector('[data-viewer-loading]');

    var dimsToggleBtn = controls && controls.querySelector('[data-viewer-dims-toggle]');
    var arStartBtn = controls && controls.querySelector('[data-viewer-ar-start]');
    var arLabelEl = arStartBtn && arStartBtn.querySelector('[data-viewer-ar-label]');

    var qrCanvas = qrModal && qrModal.querySelector('[data-viewer-qr-canvas]');
    var qrCloseBtn = qrModal && qrModal.querySelector('[data-viewer-qr-close]');

    var assetMapEl = root.querySelector('[data-b01-asset-map]');
    var colorMapEl = root.querySelector('[data-b01-color-map]');
    var assetMap = {};
    var colorMap = {};
    try { assetMap = JSON.parse(assetMapEl.textContent); } catch (e) { console.error('[b01] asset map parse failed', e); }
    try { colorMap = JSON.parse(colorMapEl.textContent); } catch (e) { console.error('[b01] color map parse failed', e); }

    var dracoDecoderUrl = assetMap['draco_decoder.js'] || '';
    var dracoBase = dracoDecoderUrl.slice(0, dracoDecoderUrl.lastIndexOf('/') + 1);

    var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    var isAndroid = /Android/.test(navigator.userAgent);
    var isMobileAR = isIOS || isAndroid;

    // The button is spelled out "Im Raum ansehen" on every viewport. It used
    // to shrink to a bare "AR" on phones, to fit a row that was then nowrap,
    // but "AR" turned out to mean nothing to most visitors. The row wraps
    // now, so a phone puts this button on a second line rather than
    // abbreviating it.
    function arIdleLabel() { return 'Im Raum ansehen'; }
    function arTitle() { return 'Im Raum ansehen'; }
    // Live-verified over USB on the actual test device that
    // navigator.deviceMemory is NOT stable across page loads on identical
    // hardware — it read 4 on several loads and 8 on another, same physical
    // phone, same Chrome build. That's documented Chrome behavior (the API
    // is deliberately fuzzed for fingerprint-resistance), not a bug, but it
    // makes deviceMemory alone unfit as the sole signal for this decision.
    // The WebGL renderer string, by contrast, was byte-identical across
    // every single test on that device ("Mali-G68") — GPU model isn't
    // something browsers obscure the same way, so it's the primary signal
    // here; deviceMemory stays as a secondary OR-condition since a low
    // reading is still meaningful even if a high one isn't reliably absent.
    function detectLowTierDevice() {
      var memLow = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
      var renderer = '';
      try {
        var probe = document.createElement('canvas');
        var gl = probe.getContext('webgl2') || probe.getContext('webgl');
        var dbg = gl && gl.getExtension('WEBGL_debug_renderer_info');
        renderer = (dbg && gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) || '';
      } catch (e) { /* WebGL probe unavailable — fall back to memLow only */ }
      // Budget/mid-range mobile GPU families known to struggle with
      // concurrent WebXR + ARCore rendering. Not exhaustive — extend this
      // list as more low-end devices get tested against real hardware
      // rather than trying to guess a complete tier database up front.
      var rendererLow = /Mali-G6[0-9]|Mali-G5[0-9]|Mali-G3[0-9]|Adreno \(TM\) 6[0-1][0-9]|Adreno \(TM\) 5[0-9][0-9]/i.test(renderer);
      return memLow || rendererLow;
    }
    var isLowTierDevice = detectLowTierDevice();

    // assets/b01-analytics.js defines this. The no-op fallback keeps every call
    // site below a plain one-liner even if that script failed to load, and
    // guarantees a reporting problem can never break the configurator itself.
    var track = window.B01Track || Object.assign(function () {}, {
      once: function () { return false; },
      resetSession: function () {},
      setContext: function () {},
    });
    track.setContext({ low_tier: isLowTierDevice });

    var viewer = null;
    var viewerPromise = null;
    var currentColorName = opts.initialColor || null;

    // A "session" is one continuous stretch with the viewer on screen. The
    // product page opens one when it switches away from photos; the B2B page
    // opens one when the viewer mounts and never closes it. The core owns the
    // numbers so first-interaction tracking does not have to reach into a mode
    // flag it cannot see.
    var sessionOpen = false;
    var sessionStartedAt = 0;
    var didInteract = false;

    function beginSession() {
      sessionOpen = true;
      sessionStartedAt = Date.now();
      didInteract = false;
      track.resetSession();
    }

    function endSession() {
      var out = {
        dwellMs: sessionStartedAt ? Date.now() - sessionStartedAt : 0,
        didInteract: didInteract,
      };
      sessionOpen = false;
      return out;
    }

    // ---- mount ----------------------------------------------------------------

    function mount() {
      if (!viewerPromise) {
        viewerPromise = window.BensbergerB01Viewer.preload(assetMap)
          .then(function () {
            return window.BensbergerB01Viewer.mount(canvas, onViewerStatus, assetMap, dracoBase);
          })
          .then(function (v) {
            viewer = v;
            v.setBackground('#ffffff');
            if (currentColorName) applyColorByName(currentColorName);
            // Maße can be pressed while the model is still loading — on the
            // B2B page the button is visible before the viewer mounts — and
            // that click found no viewer to tell. Catch the model up.
            if (dimsToggleBtn && dimsToggleBtn.classList.contains('is-active')) v.setDimensionsVisible(true);
            return v;
          })
          .catch(function (err) {
            console.error('[b01] viewer mount failed', err);
          });
      }
      return viewerPromise;
    }

    function onViewerStatus(status) {
      if (loadingEl) loadingEl.hidden = status === 'ready';
    }

    // ---- first real manipulation of the model, once per session -----------
    // Read at the DOM level rather than from the viewer's own OrbitControls:
    // it keeps the instrumentation independent of the bundle's internals, and
    // a drag the controls happen to ignore is still a visitor trying to turn
    // the chair. The threshold filters taps and scroll-throughs, which would
    // otherwise report as engagement on every mobile page that scrolls past
    // the canvas.
    (function trackFirstInteraction() {
      if (!canvas) return;
      var DRAG_THRESHOLD_PX = 6;
      var downX = 0;
      var downY = 0;
      var dragging = false;

      function markInteraction(type) {
        if (!sessionOpen || didInteract) return;
        didInteract = true;
        track.once('b01_3d_interact', {
          type: type,
          time_to_first_ms: sessionStartedAt ? Date.now() - sessionStartedAt : 0,
          context: context,
        });
      }

      canvas.addEventListener('pointerdown', function (e) {
        dragging = true;
        downX = e.clientX;
        downY = e.clientY;
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        if (Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY) < DRAG_THRESHOLD_PX) return;
        dragging = false;
        markInteraction('rotate');
      });
      canvas.addEventListener('pointerup', function () { dragging = false; });
      canvas.addEventListener('pointercancel', function () { dragging = false; });
      canvas.addEventListener('wheel', function () { markInteraction('zoom'); }, { passive: true });
      canvas.addEventListener('touchmove', function (e) {
        if (e.touches && e.touches.length >= 2) markInteraction('zoom');
      }, { passive: true });
    })();

    // ---- color ------------------------------------------------------------

    var CHROME_NAME = 'chrom';

    function applyColorByName(name) {
      currentColorName = name;
      if (!viewer) return;
      var hex = colorMap[name];
      if (!hex) return;
      var isChrome = name.trim().toLowerCase() === CHROME_NAME;
      var C = window.BensbergerB01Viewer;
      if (isChrome) {
        viewer.setMetalness(C.CHROME_METALNESS);
        viewer.setRoughness(C.CHROME_ROUGHNESS);
        viewer.setClearcoat(C.CHROME_CLEARCOAT);
        viewer.setClearcoatRoughness(C.CHROME_CLEARCOAT_ROUGHNESS);
        viewer.setColor(C.CHROME_COLOR);
      } else {
        viewer.setMetalness(C.DEFAULTS.metalness);
        viewer.setRoughness(C.DEFAULTS.roughness);
        viewer.setClearcoat(0);
        viewer.setClearcoatRoughness(0);
        viewer.setColor(hex);
      }
      viewer.reshuffleWood();
      invalidateAndroidArPrep();
    }

    // opts.track is the product page's problem: it forwards every swatch change
    // to the model even while photos are showing, so the right chair is waiting
    // if the visitor switches to 3D. Those calls are not interactions. The B2B
    // page has no photo mode, so its glue always passes track: true.
    function setColor(name, opts) {
      var previous = currentColorName;
      applyColorByName(name);
      if (opts && opts.track && previous !== name) {
        track('b01_color_change', { color: name, from_color: previous || '', context: context });
      }
    }

    // ---- Maße (dimensions overlay) ------------------------------------------

    function closeDimsFlyout() {
      if (dimsToggleBtn) dimsToggleBtn.classList.remove('is-active');
      if (viewer) viewer.setDimensionsVisible(false);
    }

    if (dimsToggleBtn) {
      dimsToggleBtn.addEventListener('click', function () {
        var willOpen = !dimsToggleBtn.classList.contains('is-active');
        dimsToggleBtn.classList.toggle('is-active', willOpen);
        if (viewer) viewer.setDimensionsVisible(willOpen);
        track('b01_dims_toggle', { state: willOpen ? 'open' : 'close', context: context });
      });
    }

    // ---- AR ------------------------------------------------------------

    // In-page WebXR runs our rendering concurrently with ARCore's own
    // camera/tracking pipeline in the same process — real, measured thermal
    // load on lower-memory devices (confirmed via Android's own thermal
    // service during live testing). The Scene Viewer hand-off instead
    // launches a separate, natively-optimized Google app and leaves the
    // browser idle, which is why it stays smooth on this same class of
    // device even when WebXR is technically supported. So: prefer WebXR
    // only when the device isn't already flagged low-tier.
    function shouldUseWebXr() {
      return webXrArSupported && !isLowTierDevice;
    }

    // Which AR hand-off a tap would take right now. Reported on both the click
    // and the result so the three paths can be compared against each other —
    // in particular how often the low-tier rule diverts a WebXR-capable phone
    // to Scene Viewer, and whether that path then succeeds more often.
    function arPath() {
      if (!isMobileAR) return 'qr_modal';
      if (isIOS) return 'quicklook';
      return shouldUseWebXr() ? 'webxr' : 'scene_viewer';
    }

    var webXrArSupported = false;
    var webXrArReadyPromise = (function () {
      try {
        if (!isAndroid || !navigator.xr) return Promise.resolve();
        return navigator.xr.isSessionSupported('immersive-ar').then(function (supported) {
          webXrArSupported = !!supported;
        }).catch(function () { webXrArSupported = false; })
          .then(function () { track.setContext({ webxr: webXrArSupported }); });
      } catch (e) {
        return Promise.resolve();
      }
    })();

    var AR_PREP_MAX_AGE_MS = 8 * 60 * 1000;
    var androidArPrepPromise = null;
    var androidArPrepTime = 0;

    function invalidateAndroidArPrep() { androidArPrepPromise = null; }

    function findAssetUrl(basename) {
      // Derived from the already-known viewer bundle <script> tag (same
      // assets/ folder, same asset_url ?v= scheme) rather than passed
      // through the asset map, since these bundles aren't 3D assets.
      var viewerScript = document.querySelector('script[src*="b01-viewer.bundle.js"]');
      if (!viewerScript) return '';
      return viewerScript.src.replace('b01-viewer.bundle.js', basename);
    }

    function loadScript(basename, type) {
      return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        if (type) script.type = type;
        script.src = findAssetUrl(basename);
        script.onload = function () { resolve(); };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    function ensureArBundle() {
      // A real ES module, not an IIFE like the other two bundles — the
      // gltf-transform/draco3dgltf packages this pulls in broke at runtime
      // on real Android hardware when force-bundled as an IIFE (confirmed
      // via the ?debug=1 overlay's log: "object is not iterable" inside the
      // dynamic-import destructuring). type="module" is required for the
      // browser to parse it as ESM at all.
      if (window.BensbergerB01AR) return Promise.resolve();
      return loadScript('b01-ar-android-v4.bundle.js', 'module');
    }

    function ensureQrBundle() {
      if (window.BensbergerB01QR) return Promise.resolve();
      return loadScript('b01-qr.bundle.js');
    }

    function prepareAndroidAR() {
      if (!isAndroid || shouldUseWebXr() || !viewer) return;
      if (androidArPrepPromise && Date.now() - androidArPrepTime < AR_PREP_MAX_AGE_MS) return;
      androidArPrepTime = Date.now();
      var prep = ensureArBundle()
        .then(function () { return viewer.exportGLB(); })
        .then(function (glb) { return window.BensbergerB01AR.uploadGlbGetModelUrl(glb, dracoBase); })
        .catch(function (err) {
          console.error('[b01] AR pre-export failed', err);
          if (androidArPrepPromise === prep) androidArPrepPromise = null;
          return null;
        });
      androidArPrepPromise = prep;
    }

    // ---- WebXR AR overlay (dom-overlay content shown over the camera feed) ---

    var xrOverlayEl = null;
    var XR_HINT_PLACE = 'Auf eine Fläche tippen, um den Stuhl zu platzieren';
    var XR_HINT_MANIPULATE = 'Ziehen zum Verschieben — mit zwei Fingern wischen zum Drehen';

    function ensureXrOverlay() {
      if (xrOverlayEl) return xrOverlayEl;
      xrOverlayEl = document.createElement('div');
      xrOverlayEl.className = 'product-3d-viewer-xr-overlay';
      xrOverlayEl.hidden = true;
      xrOverlayEl.innerHTML =
        '<button type="button" class="product-3d-viewer-xr-exit" aria-label="AR beenden">' +
        '<svg fill="none" height="16" viewBox="0 0 24 24" width="16"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>' +
        '</button>' +
        '<p class="product-3d-viewer-xr-hint" data-xr-hint></p>';
      xrOverlayEl.querySelector('.product-3d-viewer-xr-exit').addEventListener('click', function () {
        if (viewer) viewer.exitAR();
      });
      document.body.appendChild(xrOverlayEl);
      return xrOverlayEl;
    }
    function showXrOverlay() {
      var overlay = ensureXrOverlay();
      overlay.querySelector('[data-xr-hint]').textContent = XR_HINT_PLACE;
      overlay.hidden = false;
    }
    function hideXrOverlay() { if (xrOverlayEl) xrOverlayEl.hidden = true; }
    // Scoped to one AR attempt, not one 3D session: ARCore calls onPlaced on
    // every re-placement within a session (so it needs a guard at all), but a
    // visitor who leaves AR and re-enters from the same 3D view has placed the
    // chair twice and both count. runAR() re-arms this.
    var arPlacedReported = false;

    function markXrPlaced() {
      var hint = xrOverlayEl && xrOverlayEl.querySelector('[data-xr-hint]');
      if (hint) hint.textContent = XR_HINT_MANIPULATE;
      // The strongest signal the feature produces: the chair is standing in
      // someone's actual room.
      if (arPlacedReported) return;
      arPlacedReported = true;
      track('b01_ar_placed', { path: 'webxr', context: context });
    }

    var arBusy = false;
    function runAR() {
      if (arBusy || !viewer) return;
      arBusy = true;
      arPlacedReported = false;
      if (arStartBtn) arStartBtn.classList.add('is-busy');
      if (arLabelEl) arLabelEl.textContent = 'Lädt…';
      var failed = false;

      var done = function () {
        arBusy = false;
        if (arStartBtn) arStartBtn.classList.remove('is-busy');
        if (arLabelEl) arLabelEl.textContent = failed ? 'AR fehlgeschlagen' : arIdleLabel();
        if (failed && arLabelEl) setTimeout(function () { arLabelEl.textContent = arIdleLabel(); }, 2200);
      };

      // Deliberately NOT hooked into done(): the WebXR branch calls that once
      // synchronously right after enterAR() and again from its rejection
      // handler, so a result reported there would fire twice and, worse, would
      // report success before the failure was even known. Each real outcome
      // reports for itself instead, and the flag keeps the first one — which
      // is always the truthful one — from being overwritten.
      var path = arPath();
      var reported = false;
      var report = function (status, err) {
        if (reported) return;
        reported = true;
        track('b01_ar_result', {
          status: status,
          path: path,
          // 100 is GA4's own limit for an event-parameter value; trimming here
          // rather than letting GA4 truncate keeps what lands in the report
          // identical to what the browser saw.
          error: err ? String((err && err.message) || err).slice(0, 100) : '',
          context: context,
        });
      };

      if (isAndroid) {
        webXrArReadyPromise.then(function () {
          if (shouldUseWebXr()) {
            showXrOverlay();
            viewer.enterAR({ overlayRoot: ensureXrOverlay(), onEnd: onXrEnd, onPlaced: markXrPlaced, lowTier: isLowTierDevice }).then(function () {
              report('entered');
            }).catch(function (err) {
              hideXrOverlay();
              failed = true;
              report('failed', err);
              console.error('[b01] WebXR AR failed', err);
              done();
            });
            done();
          } else {
            ensureArBundle()
              .then(function () {
                return androidArPrepPromise || viewer.exportGLB().then(function (glb) {
                  return window.BensbergerB01AR.uploadGlbGetModelUrl(glb, dracoBase);
                });
              })
              .then(function (modelUrl) {
                if (!modelUrl) throw new Error('no model URL');
                window.BensbergerB01AR.navigateToSceneViewer(modelUrl, 'B01');
                report('entered');
                done();
              })
              .catch(function (err) {
                failed = true;
                report('failed', err);
                console.error('[b01] Android AR failed', err);
                done();
              });
          }
        });
      } else if (isIOS) {
        viewer.exportUSDZ().then(function (buffer) {
          var blob = new Blob([buffer], { type: 'model/vnd.usdz+zip' });
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.setAttribute('rel', 'ar');
          a.href = url;
          var img = document.createElement('img');
          a.appendChild(img);
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(function () { URL.revokeObjectURL(url); }, 120000);
          // Quick Look is a system handoff with no callback — "entered" here
          // means the export succeeded and the OS was handed the file, not
          // that the visitor saw anything. iOS AR can't be measured further
          // from the page; there is no b01_ar_placed on this path.
          report('entered');
          done();
        }).catch(function (err) {
          failed = true;
          report('failed', err);
          console.error('[b01] USDZ export failed', err);
          done();
        });
      } else {
        done(); // desktop never reaches runAR() — its click handler opens the QR modal instead
      }
    }

    function onXrEnd() {
      hideXrOverlay();
      var colorName = currentColorName;
      if (viewer) viewer.destroy();
      viewer = null;
      viewerPromise = null;
      mount().then(function () {
        if (colorName) applyColorByName(colorName);
      });
    }

    // ---- desktop QR-code AR modal --------------------------------------

    function qrTargetUrl() {
      var url = new URL(location.href);
      url.searchParams.set('ar', '1');
      return url.href;
    }

    function openQrModal() {
      if (!qrModal || !qrCanvas) return;
      ensureQrBundle().then(function () {
        window.BensbergerB01QR.toCanvas(
          qrCanvas,
          qrTargetUrl(),
          { width: 216, margin: 1, color: { dark: '#1c1c1c', light: '#ffffff' } },
          function (err) { if (err) console.error('[b01] QR render failed', err); },
        );
        qrModal.hidden = false;
        track('b01_qr_shown', { color: currentColorName || '', context: context });
      });
    }
    function closeQrModal() {
      if (qrModal) qrModal.hidden = true;
    }
    if (qrCloseBtn) qrCloseBtn.addEventListener('click', closeQrModal);
    if (qrModal) {
      qrModal.addEventListener('click', function (e) {
        if (e.target === qrModal) closeQrModal();
      });
    }

    if (arStartBtn) {
      // Restores the idle label after a launch attempt left transient state
      // ("Lädt…" / "AR fehlgeschlagen") in it — never while one is still in
      // flight, which would clobber that state mid-flight.
      var syncArLabel = function () {
        if (arBusy) return;
        if (arLabelEl) arLabelEl.textContent = arIdleLabel();
        arStartBtn.title = arTitle();
      };
      syncArLabel();

      arStartBtn.addEventListener('pointerdown', function () {
        if (!isMobileAR) return;
        mount();
        prepareAndroidAR();
      });
      arStartBtn.addEventListener('click', function () {
        track('b01_ar_click', { path: arPath(), color: currentColorName || '', context: context });
        if (isMobileAR) {
          mount().then(runAR);
        } else {
          openQrModal();
        }
      });
    }

    // ---- QR deep link: a phone that scanned the desktop QR code lands here
    // with ?ar=1 — switch straight to 3D and pre-warm the Android export, but
    // still require the user's own tap on "Im Raum ansehen" to actually launch
    // AR (Chrome only allows the Scene Viewer/Quick Look hand-off within a
    // short window of a genuine on-page gesture; a page load, even via a
    // deep link, doesn't count as one).
    function handleDeepLink() {
      var params = new URLSearchParams(location.search);
      if (params.get('ar') !== '1' || !isMobileAR) return;
      // Fires before onDeepLink so the landing is recorded even if the caller's
      // switch to 3D throws — this is the one event that proves a desktop QR code
      // actually reached a phone.
      track('b01_qr_landing', { context: context });
      if (onDeepLink) onDeepLink();
      mount().then(function () { prepareAndroidAR(); });
    }

    function destroy() {
      hideXrOverlay();
      closeQrModal();
      if (viewer) viewer.destroy();
      viewer = null;
      viewerPromise = null;
    }

    return {
      mount: mount,
      setColor: setColor,
      beginSession: beginSession,
      endSession: endSession,
      closeDims: closeDimsFlyout,
      handleDeepLink: handleDeepLink,
      invalidateArPrep: invalidateAndroidArPrep,
      isMounted: function () { return !!viewer; },
      destroy: destroy,
    };
  }

  window.BensbergerB01Core = { create: create };
})();
